import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, AlertCircle, Globe, Trash2, Folder, FolderPlus, Edit2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPageFull = () => {
  const { token } = useAuth();
  const [message, setMessage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('terms'); // 'terms' | 'categories' | 'users'
  const [selectedUserAnalyses, setSelectedUserAnalyses] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null); // Редактируемая категория
  
  // Новая/редактируемая категория
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    name_en: '',
    name_kk: '',
    definition: '',
    definition_en: '',
    definition_kk: '',
    scope_note: '',
    use_for: '',
    narrower_terms: '',
    related_terms: '',
    preferred_term: '',
    lexical_variants: '',
    used_for: ''
  });
  
  const [newTerm, setNewTerm] = useState({
    // Preferred terms на 3 языках
    preferred_term_ru: '',
    preferred_term_en: '',
    preferred_term_kk: '',
    
    // Основные поля
    category: '',
    definition: '',
    scope_note: '',
    
    // SKOS отношения
    use_for: '',           // UF - Use For (синонимы)
    broader_terms: '',     // BT - Broader Terms
    narrower_terms: '',    // NT - Narrower Terms
    related_terms: '',     // RT - Related Terms
    top_terms: '',         // TT - Top Terms
    preferred_term: '',    // PT - Preferred Term
    lexical_variants: '',  // LE - Lexical Equivalent
    used_for: ''           // USE - Used For
  });

  // Функция загрузки категорий
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Загрузка категорий при монтировании
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Загрузка пользователей
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setMessage({ type: 'error', text: 'Ошибка при загрузке пользователей' });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChangeUserRole = async (userId, newRole) => {
    if (!window.confirm(`Вы уверены, что хотите изменить роль пользователя на ${newRole}?`)) {
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/users/${userId}/role?new_role=${newRole}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: `Роль изменена на ${newRole}` });
      fetchUsers();
    } catch (err) {
      console.error('Error changing role:', err);
      setMessage({ type: 'error', text: 'Ошибка при изменении роли: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя? Это действие необратимо.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Пользователь удален' });
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setMessage({ type: 'error', text: 'Ошибка при удалении: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      name_en: '',
      name_kk: '',
      definition: '',
      definition_en: '',
      definition_kk: '',
      scope_note: '',
      use_for: '',
      narrower_terms: '',
      related_terms: '',
      preferred_term: '',
      lexical_variants: '',
      used_for: ''
    });
    setEditingCategory(null);
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name || '',
      name_en: cat.name_en || '',
      name_kk: cat.name_kk || '',
      definition: cat.definition || '',
      definition_en: cat.definition_en || '',
      definition_kk: cat.definition_kk || '',
      scope_note: cat.scope_note || '',
      use_for: (cat.use_for || []).join(', '),
      narrower_terms: (cat.narrower_terms || []).join(', '),
      related_terms: (cat.related_terms || []).join(', '),
      preferred_term: cat.preferred_term || '',
      lexical_variants: (cat.lexical_variants || []).join(', '),
      used_for: (cat.used_for || []).join(', ')
    });
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!categoryForm.name.trim()) {
      setMessage({ type: 'error', text: 'Обязательно укажите название категории' });
      return;
    }

    const categoryData = {
      name: categoryForm.name.trim(),
      name_en: categoryForm.name_en.trim() || '',
      name_kk: categoryForm.name_kk.trim() || '',
      definition: categoryForm.definition.trim() || '',
      definition_en: categoryForm.definition_en.trim() || '',
      definition_kk: categoryForm.definition_kk.trim() || '',
      scope_note: categoryForm.scope_note.trim() || '',
      use_for: categoryForm.use_for.split(',').map(s => s.trim()).filter(s => s.length > 0),
      narrower_terms: categoryForm.narrower_terms.split(',').map(s => s.trim()).filter(s => s.length > 0),
      related_terms: categoryForm.related_terms.split(',').map(s => s.trim()).filter(s => s.length > 0),
      preferred_term: categoryForm.preferred_term.trim() || '',
      lexical_variants: categoryForm.lexical_variants.split(',').map(s => s.trim()).filter(s => s.length > 0),
      used_for: categoryForm.used_for.split(',').map(s => s.trim()).filter(s => s.length > 0)
    };

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Категория успешно обновлена!' });
      } else {
        await axios.post(`${API}/categories`, categoryData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Категория успешно добавлена!' });
      }
      resetCategoryForm();
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Ошибка: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
      return;
    }

    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Категория удалена' });
      if (editingCategory && editingCategory.id === categoryId) {
        resetCategoryForm();
      }
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Ошибка при удалении категории' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Проверка обязательных полей
    if (!newTerm.preferred_term_ru.trim()) {
      setMessage({ type: 'error', text: 'Обязательно заполните термин на русском языке' });
      return;
    }

    if (!newTerm.category) {
      setMessage({ type: 'error', text: 'Обязательно выберите категорию' });
      return;
    }

    try {
      // Преобразуем строки в массивы
      const termData = {
        preferred_term_ru: newTerm.preferred_term_ru.trim(),
        preferred_term_en: newTerm.preferred_term_en.trim() || null,
        preferred_term_kk: newTerm.preferred_term_kk.trim() || null,
        category: newTerm.category,
        definition: newTerm.definition.trim() || null,
        scope_note: newTerm.scope_note.trim() || null,
        
        // Преобразуем в массивы (разделяем по запятой)
        use_for: newTerm.use_for.split(',').map(s => s.trim()).filter(s => s.length > 0),
        used_for: newTerm.used_for.split(',').map(s => s.trim()).filter(s => s.length > 0),
        broader_terms: newTerm.broader_terms.split(',').map(s => s.trim()).filter(s => s.length > 0),
        narrower_terms: newTerm.narrower_terms.split(',').map(s => s.trim()).filter(s => s.length > 0),
        related_terms: newTerm.related_terms.split(',').map(s => s.trim()).filter(s => s.length > 0),
        top_terms: newTerm.top_terms.split(',').map(s => s.trim()).filter(s => s.length > 0),
        lexical_variants: newTerm.lexical_variants.split(',').map(s => s.trim()).filter(s => s.length > 0),
        
        preferred_term: newTerm.preferred_term.trim() || null
      };

      await axios.post(`${API}/thesaurus`, termData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Термин успешно добавлен!' });
      
      // Очистить форму
      setNewTerm({
        preferred_term_ru: '',
        preferred_term_en: '',
        preferred_term_kk: '',
        category: categories.length > 0 ? categories[0].name : '',
        definition: '',
        scope_note: '',
        use_for: '',
        used_for: '',
        broader_terms: '',
        narrower_terms: '',
        related_terms: '',
        top_terms: '',
        preferred_term: '',
        lexical_variants: ''
      });
    } catch (err) {
      console.error('Error adding term:', err);
      setMessage({ type: 'error', text: 'Ошибка при добавлении термина: ' + (err.response?.data?.detail || err.message) });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Административная панель
          </h1>
          <p className="text-lg text-gray-600">
            Управление категориями и терминами тезауруса
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('terms')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'terms'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Добавить термин
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'categories'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Folder className="w-4 h-4 inline mr-2" />
            Управление категориями
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            Управление пользователями
          </button>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`p-4 rounded-lg mb-6 flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            <AlertCircle className="w-5 h-5" />
            {message.text}
          </div>
        )}

        {/* Categories Management Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            {/* Category Form - Add/Edit */}
            <form onSubmit={handleSaveCategory} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  {editingCategory ? <Edit2 className="w-5 h-5" /> : <FolderPlus className="w-5 h-5" />}
                  {editingCategory ? 'Редактировать категорию' : 'Добавить новую категорию'}
                </h2>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={resetCategoryForm}
                    className="text-gray-500 hover:text-gray-700 p-2"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Названия на 3 языках */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Названия
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Название (русский) *
                    </label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="Например: Оружие и техника"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name (English)
                    </label>
                    <input
                      type="text"
                      value={categoryForm.name_en}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name_en: e.target.value })}
                      placeholder="E.g.: Weapons and Equipment"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Атауы (Қазақша)
                    </label>
                    <input
                      type="text"
                      value={categoryForm.name_kk}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name_kk: e.target.value })}
                      placeholder="Мысалы: Қару және техника"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              
              {/* Определения на 3 языках */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Определение (Definition)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Определение (русский)
                    </label>
                    <textarea
                      value={categoryForm.definition}
                      onChange={(e) => setCategoryForm({ ...categoryForm, definition: e.target.value })}
                      placeholder="Описание категории на русском языке..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Definition (English)
                      </label>
                      <textarea
                        value={categoryForm.definition_en}
                        onChange={(e) => setCategoryForm({ ...categoryForm, definition_en: e.target.value })}
                        placeholder="Category description in English..."
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Анықтама (Қазақша)
                      </label>
                      <textarea
                        value={categoryForm.definition_kk}
                        onChange={(e) => setCategoryForm({ ...categoryForm, definition_kk: e.target.value })}
                        placeholder="Санат сипаттамасы қазақ тілінде..."
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Scope Note */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  <span className="text-yellow-600">SN</span> - Scope Note (Примечание)
                </h3>
                <textarea
                  value={categoryForm.scope_note}
                  onChange={(e) => setCategoryForm({ ...categoryForm, scope_note: e.target.value })}
                  placeholder="Дополнительные примечания к использованию категории..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* SKOS Отношения */}
              <div className="pb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">SKOS Отношения</h3>
                <p className="text-sm text-gray-500 mb-4">Разделяйте термины запятой</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-blue-600 font-bold">UF</span> - Use For (синонимы категории)
                    </label>
                    <input
                      type="text"
                      value={categoryForm.use_for}
                      onChange={(e) => setCategoryForm({ ...categoryForm, use_for: e.target.value })}
                      placeholder="синоним1, синоним2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-purple-600 font-bold">NT</span> - Narrower Terms (подкатегории)
                    </label>
                    <input
                      type="text"
                      value={categoryForm.narrower_terms}
                      onChange={(e) => setCategoryForm({ ...categoryForm, narrower_terms: e.target.value })}
                      placeholder="подкатегория1, подкатегория2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-orange-600 font-bold">RT</span> - Related Terms (связанные)
                    </label>
                    <input
                      type="text"
                      value={categoryForm.related_terms}
                      onChange={(e) => setCategoryForm({ ...categoryForm, related_terms: e.target.value })}
                      placeholder="связанная категория1, связанная категория2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-pink-600 font-bold">LE</span> - Lexical Equivalent (эквиваленты)
                    </label>
                    <input
                      type="text"
                      value={categoryForm.lexical_variants}
                      onChange={(e) => setCategoryForm({ ...categoryForm, lexical_variants: e.target.value })}
                      placeholder="weapons, armes"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              
              {/* Кнопки */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  {editingCategory ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  {editingCategory ? 'Сохранить изменения' : 'Добавить категорию'}
                </button>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={resetCategoryForm}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Отмена
                  </button>
                )}
              </div>
            </form>

            {/* Categories List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Список категорий ({categories.length})
              </h2>
              
              {categories.length > 0 ? (
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                        editingCategory?.id === cat.id 
                          ? 'bg-blue-50 border-2 border-blue-300' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Folder className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900">{cat.name}</div>
                          <div className="text-sm text-gray-500 flex flex-wrap gap-2">
                            {cat.name_en && <span>EN: {cat.name_en}</span>}
                            {cat.name_kk && <span>KK: {cat.name_kk}</span>}
                          </div>
                          {cat.definition && (
                            <div className="text-sm text-gray-600 mt-1 truncate max-w-md">
                              {cat.definition.substring(0, 80)}{cat.definition.length > 80 ? '...' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEditCategory(cat)}
                          className="text-blue-500 hover:text-blue-700 transition-colors p-2"
                          title="Редактировать"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="text-red-500 hover:text-red-700 transition-colors p-2"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  Категории не найдены. Добавьте первую категорию выше.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Terms Form Tab */}
        {activeTab === 'terms' && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
            
            {/* Preferred Terms - 3 языка */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Preferred Terms (основной термин)
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Русский (обязательно) *
                  </label>
                  <input
                    type="text"
                    value={newTerm.preferred_term_ru}
                    onChange={(e) => setNewTerm({ ...newTerm, preferred_term_ru: e.target.value })}
                    placeholder="Например: Танк"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      English (опционально)
                    </label>
                    <input
                      type="text"
                      value={newTerm.preferred_term_en}
                      onChange={(e) => setNewTerm({ ...newTerm, preferred_term_en: e.target.value })}
                      placeholder="E.g.: Tank"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Қазақша (опционально)
                    </label>
                    <input
                      type="text"
                      value={newTerm.preferred_term_kk}
                      onChange={(e) => setNewTerm({ ...newTerm, preferred_term_kk: e.target.value })}
                      placeholder="Мысалы: Танк"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Основная информация */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Основная информация</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Категория (Микротезаурус) *
                  </label>
                  <select
                    value={newTerm.category}
                    onChange={(e) => setNewTerm({ ...newTerm, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Выберите категорию</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Нет нужной категории? <button type="button" onClick={() => setActiveTab('categories')} className="text-blue-600 hover:underline">Добавьте её</button>
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Definition (определение)
                  </label>
                  <textarea
                    value={newTerm.definition}
                    onChange={(e) => setNewTerm({ ...newTerm, definition: e.target.value })}
                    placeholder="Подробное определение термина..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SN - Scope Note (примечание)
                  </label>
                  <textarea
                    value={newTerm.scope_note}
                    onChange={(e) => setNewTerm({ ...newTerm, scope_note: e.target.value })}
                    placeholder="Дополнительные примечания..."
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            
            {/* SKOS Отношения */}
            <div className="pb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">SKOS Отношения</h2>
              <p className="text-sm text-gray-500 mb-4">
                Разделяйте термины запятой. <strong>BT</strong> определяет иерархию — если указанный BT не существует, он станет веткой в категории.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-blue-600 font-bold">UF</span> - Use For (синонимы)
                  </label>
                  <input
                    type="text"
                    value={newTerm.use_for}
                    onChange={(e) => setNewTerm({ ...newTerm, use_for: e.target.value })}
                    placeholder="синоним1, синоним2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-green-600 font-bold">BT</span> - Broader Terms (широкие термины / ветка иерархии)
                  </label>
                  <input
                    type="text"
                    value={newTerm.broader_terms}
                    onChange={(e) => setNewTerm({ ...newTerm, broader_terms: e.target.value })}
                    placeholder="Холодное оружие, Складные ножи"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Эти термины станут ветками в иерархии
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-purple-600 font-bold">NT</span> - Narrower Terms (узкие термины)
                  </label>
                  <input
                    type="text"
                    value={newTerm.narrower_terms}
                    onChange={(e) => setNewTerm({ ...newTerm, narrower_terms: e.target.value })}
                    placeholder="дочерний термин1, дочерний термин2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-orange-600 font-bold">RT</span> - Related Terms (связанные термины)
                  </label>
                  <input
                    type="text"
                    value={newTerm.related_terms}
                    onChange={(e) => setNewTerm({ ...newTerm, related_terms: e.target.value })}
                    placeholder="связанный термин1, связанный термин2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-600 font-bold">TT</span> - Top Terms (главные термины)
                  </label>
                  <input
                    type="text"
                    value={newTerm.top_terms}
                    onChange={(e) => setNewTerm({ ...newTerm, top_terms: e.target.value })}
                    placeholder="главный термин"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-indigo-600 font-bold">PT</span> - Preferred Term (предпочтительный термин)
                  </label>
                  <input
                    type="text"
                    value={newTerm.preferred_term}
                    onChange={(e) => setNewTerm({ ...newTerm, preferred_term: e.target.value })}
                    placeholder="Используйте: термин"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-pink-600 font-bold">LE</span> - Lexical Equivalent (лексический эквивалент)
                  </label>
                  <input
                    type="text"
                    value={newTerm.lexical_variants}
                    onChange={(e) => setNewTerm({ ...newTerm, lexical_variants: e.target.value })}
                    placeholder="tank, tanque, char"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-teal-600 font-bold">USE</span> - Used For (используется для)
                  </label>
                  <input
                    type="text"
                    value={newTerm.used_for}
                    onChange={(e) => setNewTerm({ ...newTerm, used_for: e.target.value })}
                    placeholder="термин1, термин2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg flex items-center justify-center gap-2"
            >
              <Plus className="w-6 h-6" />
              Добавить термин
            </button>
          </form>
        )}

        {/* Users Management Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Управление пользователями</h2>
            
            {users.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Пользователи не найдены
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900">Имя пользователя</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900">Email</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900">Роль</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900">Статус</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900">Дата создания</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{user.username}</td>
                        <td className="px-6 py-4 text-gray-600">{user.email}</td>
                        <td className="px-6 py-4">
                          <select
                            value={user.role}
                            onChange={(e) => handleChangeUserRole(user.id, e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="guest">Guest (10 попыток/5ч)</option>
                            <option value="user">User (100 попыток/5ч)</option>
                            <option value="admin">Admin (без ограничений)</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            user.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Активен' : 'Неактивен'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {new Date(user.created_at).toLocaleDateString('ru-RU')}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4 inline" />
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPageFull;
